import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, Trash2, Webhook, CheckCircle, XCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface WebhookType {
  id: string
  url: string
  events: string[]
  secret: string
  is_active: boolean
}

const AVAILABLE_EVENTS = ['order.created', 'order.updated', 'product.created', 'product.updated', 'customer.created']

export function WebhooksPage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({ url: '', events: [] as string[], secret: '' })

  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ['webhooks', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from('webhooks').select('*').eq('store_id', currentStore?.id).order('created_at', { ascending: false })
      if (error) throw error
      return data as WebhookType[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from('webhooks').insert({ store_id: currentStore?.id, ...data })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast.success('Webhook created')
      handleClose()
    },
    onError: () => toast.error('Failed to create webhook'),
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('webhooks').update({ is_active }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast.success('Webhook status updated')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('webhooks').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast.success('Webhook deleted')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.url || formData.events.length === 0) {
      toast.error('URL and at least one event are required')
      return
    }
    createMutation.mutate(formData)
  }

  const handleClose = () => {
    setOpen(false)
    setFormData({ url: '', events: [], secret: '' })
  }

  const generateSecret = () => {
    const secret = 'whsec_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    setFormData({ ...formData, secret })
  }

  const toggleEvent = (event: string) => {
    setFormData({
      ...formData,
      events: formData.events.includes(event) ? formData.events.filter(e => e !== event) : [...formData.events, event]
    })
  }

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Webhooks"
          description="Configure webhooks to receive real-time events"
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </Button>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : webhooks.length === 0 ? (
          <EmptyState icon={Webhook} title="No webhooks configured" description="Set up webhooks to receive real-time notifications" action={{ label: 'Add Webhook', onClick: () => setOpen(true) }} />
        ) : (
          <div className="space-y-4">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="bg-white rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {webhook.is_active ? <CheckCircle className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-gray-400" />}
                      <span className="font-mono text-sm">{webhook.url}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {webhook.events.map((event) => (
                        <span key={event} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{event}</span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Secret: {webhook.secret.substring(0, 20)}...</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant={webhook.is_active ? 'default' : 'outline'} onClick={() => toggleMutation.mutate({ id: webhook.id, is_active: !webhook.is_active })}>
                      {webhook.is_active ? 'Active' : 'Inactive'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteMutation.mutate(webhook.id)} disabled={deleteMutation.isPending}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Webhook</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Webhook URL *</Label>
                <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://your-app.com/webhooks" required />
              </div>
              <div>
                <Label>Events *</Label>
                <div className="space-y-2 mt-2">
                  {AVAILABLE_EVENTS.map((event) => (
                    <label key={event} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.events.includes(event)} onChange={() => toggleEvent(event)} className="rounded" />
                      <span className="text-sm">{event}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Webhook Secret</Label>
                <div className="flex gap-2">
                  <Input value={formData.secret} onChange={(e) => setFormData({ ...formData, secret: e.target.value })} placeholder="whsec_..." />
                  <Button type="button" variant="outline" onClick={generateSecret}>Generate</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Use this to verify webhook signatures</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  )
}
